import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import firebase from "gatsby-plugin-firebase";

// Helper functions
const setNestedField = (state, path, val) => {
  // This will retain any existing values along the way
  const keys = path.split(".");
  const lastKey = keys.pop();
  const lastObj = keys.reduce((obj, key) => (obj[key] = obj[key] || {}), state);

  if (!val) {
    // value is falsy, so delete the field
    delete lastObj[lastKey];
  } else {
    lastObj[lastKey] = val;
  }
};

// Initial state
export const initialState = {
  allUsers: {},
  isLoading: false,
  hasError: false,
  hasFulfilled: false,
  idsFulfilled: [],
};

// Thunks
export const loadProfile = createAsyncThunk(
  "users/getProfile",
  async (username) => {
    try {
      const querySnapshot = await firebase
        .firestore()
        .collection("public-profiles")
        .where("username", "==", username)
        .limit(1)
        .get();
      if (querySnapshot.empty) {
        // User's public profile doesn't exist
        return { username: username };
      } else {
        // We set a query limit of 1, so access the single document
        const snapshot = querySnapshot.docs[0];
        return {
          username: username,
          data: { uid: snapshot.id, ...snapshot.data() },
        };
      }
    } catch (error) {
      // Log an event
      firebase.analytics().logEvent("load_user_profile", {
        errorCode: error.code,
        errorMsg: error.message,
      });
      // Return as if the user's public profile doesn't exist
      return { username: username };
    }
  }
);

export const getProfileById = createAsyncThunk(
  "users/getProfileById",
  async (uid) => {
    try {
      const snapshot = await firebase
        .firestore()
        .collection("public-profiles")
        .doc(uid)
        .get();
      if (!snapshot.exists) {
        // User's public profile doesn't exist
        return { uid };
      } else {
        const data = snapshot.data();
        return {
          username: data.username,
          data: { uid: snapshot.id, ...data },
        };
      }
    } catch (error) {
      // Log an event
      firebase.analytics().logEvent("load_user_profile", {
        errorCode: error.code,
        errorMsg: error.message,
      });

      // Return as if the user's public profile doesn't exist
      return { uid };
    }
  }
);

const getSentRecommendations = async (uid) => {
  const querySnapshot = await firebase
    .firestore()
    .collection("referral-snippets")
    .where(`${uid}`, "!=", false)
    .get();

  if (querySnapshot.empty) {
    // User hasn't sent any recommendations
    return null;
  } else {
    const sent = [];
    let snippet;

    for (const doc of querySnapshot.docs) {
      snippet = (
        await firebase.firestore().collection("user-snippets").doc(doc.id).get()
      ).data();
      sent.push({
        username: snippet.username,
        photo: snippet.photo,
      });
    }

    return sent;
  }
};

const getReceivedRecommendations = async (uid) => {
  const snapshotRef = await firebase
    .firestore()
    .collection("referral-snippets")
    .doc(uid)
    .get();

  if (snapshotRef.exists) {
    const snapshot = snapshotRef.data();

    if (Object.keys(snapshot).length !== 0) {
      const received = [];
      let snippet;
      for (const user of Object.keys(snapshot)) {
        snippet = (
          await firebase.firestore().collection("user-snippets").doc(user).get()
        ).data();
        received.push({
          username: snippet.username,
          photo: snippet.photo,
          text: snapshot[user],
        });
      }

      return received;
    } else {
      // There are no snippets, so return null
      return null;
    }
  } else {
    // Member hasn't accepted any recommendations
    return null;
  }
};

export const loadRecommendations = createAsyncThunk(
  "users/getRecommendations",
  async (username) => {
    try {
      // Get the uid from the username
      const currentUserSnippetQuery = await firebase
        .firestore()
        .collection("user-snippets")
        .where("username", "==", username)
        .get();

      // Check if user exists before fetching recommendations
      if (currentUserSnippetQuery.empty) {
        return { username: username, sent: null, received: null };
      }

      const uid = currentUserSnippetQuery.docs[0].id;

      // Create promises, then await in parallel for performance
      const sentRecPromise = getSentRecommendations(uid);
      const receivedRecPromise = getReceivedRecommendations(uid);

      const [sent, received] = await Promise.all([
        sentRecPromise,
        receivedRecPromise,
      ]);

      return { username: username, sent: sent, received: received };
    } catch (error) {
      firebase.analytics().logEvent("load_recommendations", {
        errorCode: error.code,
        errorMsg: error.message,
      });
      return error;
    }
  }
);

// Slice
export const usersSlice = createSlice({
  name: "users",
  initialState: initialState,
  reducers: {
    setUser: (state, action) => {
      state.allUsers[action.payload.username] = action.payload.data;
    },
    updateUser: (state, action) => {
      const payload = action.payload;
      if (state.allUsers[payload.username] !== undefined) {
        // Only update if the user's profile has already been downloaded
        // This ensures we fetch from the database the first time they
        // visit their own profile
        setNestedField(
          state.allUsers[payload.username],
          payload.path,
          payload.value
        );
      }
    },
  },
  extraReducers: {
    [loadProfile.pending]: (state, action) => {
      state.isLoading = true;
      state.hasError = false;
      state.hasFulfilled = false;
    },
    [loadProfile.fulfilled]: (state, action) => {
      if (action.payload.data) {
        state.allUsers[action.payload.username] = action.payload.data;
        state.idsFulfilled = [...state.idsFulfilled, action.payload.data.uid];
      }
      setNestedField(
        state.allUsers,
        `${action.payload.username}.isLoading`,
        false
      );
      setNestedField(
        state.allUsers,
        `${action.payload.username}.hasError`,
        false
      );
      setNestedField(
        state.allUsers,
        `${action.payload.username}.hasFulfilled`,
        true
      );
    },
    [getProfileById.fulfilled]: (state, action) => {
      if (action.payload.data) {
        state.allUsers[action.payload.username] = action.payload.data;
        state.idsFulfilled = [...state.idsFulfilled, action.payload.data.uid];
        setNestedField(
          state.allUsers,
          `${action.payload.username}.isLoading`,
          false
        );
        setNestedField(
          state.allUsers,
          `${action.payload.username}.hasError`,
          false
        );
        setNestedField(
          state.allUsers,
          `${action.payload.username}.hasFulfilled`,
          true
        );
      } else {
        // returned uid only, push fulfilled ids
        state.idsFulfilled = [...state.idsFulfilled, action.payload.uid];
      }
    },
    [loadProfile.rejected]: (state, action) => {
      state.isLoading = false;
      state.hasError = true;
      state.hasFulfilled = false;
    },
    [loadRecommendations.fulfilled]: (state, action) => {
      if (action.payload.sent) {
        setNestedField(
          state.allUsers,
          `${action.payload.username}.sent`,
          action.payload.sent
        );
      }
      if (action.payload.received) {
        setNestedField(
          state.allUsers,
          `${action.payload.username}.received`,
          action.payload.received
        );
      }
    },
  },
});

// Selectors
export const selectAllUsers = (state) => state.users.allUsers;
export const selectUser = (state, username) => state.users.allUsers[username];
export const selectHasFulfilled = (state, username) => {
  if (state.users.allUsers[username]) {
    return state.users.allUsers[username].hasFulfilled;
  } else {
    return false;
  }
};
export const selectUserById = (state, id) =>
  Object.values(state.users.allUsers).find((user) => user.uid === id);
export const selectHasFulfilledById = (state, id) =>
  state.users.idsFulfilled.includes(id);
export const selectCandidatesToGet = (state, jobData, candidateType) => {
  if (jobData === undefined) {
    // Occurs on refresh before the job post can be loaded
    return undefined;
  } else if (
    candidateType === "applicant" &&
    jobData.applicants !== undefined
  ) {
    // Return a list of uids not included in state.users.allUsers
    const candidateUids = jobData.applicants.map((candidate) => candidate.uid);
    return candidateUids.filter(
      (uid) => !state.users.idsFulfilled.includes(uid)
    );
  } else if (candidateType === "favorite" && jobData.trackedBy !== undefined) {
    // trackedBy is an array of uids as opposed to an array of objects
    return jobData.trackedBy.filter(
      (uid) => !state.users.idsFulfilled.includes(uid)
    );
  } else {
    // Type is specified but the array is undefined
    return undefined;
  }
};
export const selectCandidateProfiles = (state, jobData, candidateType) => {
  if (jobData === undefined) {
    // Occurs on refresh before the job post can be loaded
    return undefined;
  } else if (
    candidateType === "applicant" &&
    jobData.applicants !== undefined
  ) {
    const candidateUids = jobData.applicants.map((candidate) => candidate.uid);
    return Object.values(state.users.allUsers).filter((user) =>
      candidateUids.includes(user.uid)
    );
  } else if (candidateType === "favorite" && jobData.trackedBy !== undefined) {
    return Object.values(state.users.allUsers).filter((user) =>
      jobData.trackedBy.includes(user.uid)
    );
  } else {
    // Type is specified but the array is undefined
    return undefined;
  }
};

// Exports
export const { setUser, updateUser } = usersSlice.actions;
export default usersSlice.reducer;
